import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { showSuccess, showError } from '../utils/notifications';
import API_CONFIG from '../config';
import { translateErrorMessage } from '../utils/errorTranslations';

const Register = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
  });
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.confirmPassword) {
      showError("Les mots de passe ne correspondent pas");
      return;
    }
    
    setLoading(true);
    
    try {
      const response = await fetch(`${API_CONFIG.BASE_URL}/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          password: formData.password,
          confirm_password: formData.confirmPassword,
          first_name: formData.firstName
        }),
      });
      
      const data = await response.json();
      
      if (response.ok) {
        // Afficher un message de succès
        showSuccess('Votre compte a été créé avec succès! Redirection vers la page de connexion...');
        
        // Redirection vers la page de connexion après 3 secondes
        setTimeout(() => {
          navigate('/login');
        }, 3000);
      } else {
        // Gérer les erreurs de validation (422) et autres erreurs
        let errorMessage = 'Erreur lors de l\'inscription';
        
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            // Erreurs de validation (422) - extraire le premier message
            const firstError = data.detail[0];
            if (firstError && firstError.msg) {
              errorMessage = firstError.msg;
            }
          } else if (typeof data.detail === 'string') {
            // Erreur simple (string)
            errorMessage = data.detail;
          }
        }
        
        // Traduire et afficher l'erreur
        const translatedError = translateErrorMessage(errorMessage);
        showError(translatedError);
      }
    } catch (error) {
      showError('Erreur réseau, veuillez réessayer');
      console.error('Register error:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-50 via-white to-purple-50 px-4">
      {/* Élément décoratif en haut */}
      <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-primary/10 to-transparent"></div>
      
      <div className="max-w-md w-full space-y-3 z-10" style={{transform: 'translateY(-25%)'}}>
        <div className="flex flex-col items-center">
          {/* Logo pour tous les écrans */}
          <img 
            src="/images/logo-full.png" 
            alt="Mes Prix" 
            className="h-48 sm:h-48 lg:h-48 w-auto mx-auto" 
          />
          <h2 className="mt-1 text-center text-lg sm:text-xl font-extrabold text-gray-900">
            Créer un compte
          </h2>
          <p className="mt-1 text-center text-sm text-gray-600">
            Rejoignez la communauté Mes Prix
          </p>
        </div>
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="rounded-md shadow-sm -space-y-px">
            <div>
              <label htmlFor="firstName" className="sr-only">Prénom</label>
              <input
                id="firstName"
                name="firstName"
                type="text"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-t-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Prénom"
                value={formData.firstName}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="email" className="sr-only">Email</label>
              <input
                id="email"
                name="email"
                type="email"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Email"
                value={formData.email}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="password" className="sr-only">Mot de passe</label>
              <input
                id="password"
                name="password"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Mot de passe"
                value={formData.password}
                onChange={handleChange}
              />
            </div>
            <div>
              <label htmlFor="confirmPassword" className="sr-only">Confirmer le mot de passe</label>
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                required
                className="appearance-none rounded-none relative block w-full px-3 py-2 border border-gray-300 placeholder-gray-500 text-gray-900 rounded-b-md focus:outline-none focus:ring-primary focus:border-primary focus:z-10 sm:text-sm"
                placeholder="Confirmer le mot de passe"
                value={formData.confirmPassword}
                onChange={handleChange}
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              className="group relative w-full flex justify-center py-2 px-4 border border-transparent text-sm font-medium rounded-md text-white bg-primary hover:bg-primary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              disabled={loading}
            >
              {loading ? 'Création en cours...' : 'Créer un compte'}
            </button>
          </div>
          
          <div className="text-sm text-center">
            <Link to="/login" className="font-medium text-primary hover:text-primary-dark">
              Déjà un compte ? Se connecter
            </Link>
          </div>
        </form>
      </div>
      
      {/* Footer en bas */}
      <div className="absolute bottom-0 left-0 w-full bg-gradient-to-t from-gray-100/80 to-transparent p-6">
        <div className="text-center">
          <p className="text-xs text-gray-500 mb-2">© 2025 Mes Prix - Tous droits réservés</p>
          <div className="flex justify-center space-x-4 text-xs text-gray-400">
            <span>Conditions d'utilisation</span>
            <span>•</span>
            <span>Politique de confidentialité</span>
            <span>•</span>
            <span>Support</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register; 